define((require) => {
    "use strict";

    const _       = require("underscore");
    const Browser = require("shared/services/Browser");
    const Class   = require("shared/util/Class");

    /**
     * @private @type {String}
     * Prefix to any key stored in local or session storage as this class may be run at the client domain, i.e. in
     * DnD or the viewer. We do not want to conflict with any existing keys there.
     */
    const KEY_PREFIX = "zmags_"; // someprefix + underscore seems to be the naming convention

    /**
     * The storage class will index distinct sub-trees of properties based on specific keys. This means that the
     * same property names can be reused across keys. <p>
     *
     * Since local storage stores strings, the stored representation is json. Hence, any value tried stored must
     * be json formattable.
     */
    const Storage = Class.extend({

        /**
         * @private @type {Storage}
         * The backing storage: local or session storage.
         */
        _storage: null,

        /**
         * @private @type {Object}
         * Copy of what's in <tt>_storage</tt>.
         *
         * Will be built up as user is asking for different fields.
         */
        _attributes: null, // TODO: this will lead to wrong in-mem state if two different windows update the storage, fx. editor and DnD!

        /**
         * Constructor.
         *
         * @param {Storage} storage The storage to use; never null.
         */
        constructor(storage) {
            this._storage = storage;
            this._attributes = {};
        },

        /**
         * @private
         * Fetches all stored properties under the specified key and updates this storage accordingly.
         *
         * @param {String} key The fully qualified key to fetch data for, e.g. some key defined in
         *                     <tt>Storage.Keys</tt> plus some postfix; never null.
         *
         * @return {*} The properties; the in-memory representation will also be updated by this.
         */
        _getProperties(key) {
            if (!this._attributes.hasOwnProperty(key)) {
                let properties;

                try {
                    properties = JSON.parse(this._storage.getItem(KEY_PREFIX + key) || this._storage.getItem(key)); // TODO: legacy issue looking up without prefix, remove eventually in the future...

                // Catch all, as we do not want this storage to interfere with normal behaviour otherwise...
                } catch (e) {
                    // Fall-through, which will default...
                }

                this._attributes[key] = properties || {};
            }

            return this._attributes[key];
        },

        /**
         * @private
         * Helper function to sync a part of `attributes` with storage. <p>
         *
         * On Safari, incognito mode is enforced by setting the quota to zero whenever writing to local storage. This
         * will raise an exception (and for example cause login to fail since it relies on local/session storage). In
         * addition, a quota exceeded exception can be raised at any time if the actual quota is exceeded, so we have
         * to catch errors in the general case anyways. On errors, we return false from here, so the caller can check
         * if the sync was successful or not.
         *
         * @param {String}   key The fully qualified key to sync, e.g. some key defined in <tt>Storage.Keys</tt> plus
         *                        some postfix; never null.
         *
         * @return {Boolean} True if the sync was a success, false otherwise.
         */
        _sync(key) {
            try {
                this._storage.setItem(KEY_PREFIX + key, JSON.stringify(this._attributes[key] || {}));

                return true;
            } catch (e) {
                return false;
            }
        },

        /**
         * Updates the property or properties in the sub-tree indexed by the specified key, by adding new
         * keys to it or update the ones that overlap.
         *
         * @param {String}   key        The key for the property sub-tree were to update.
         * @param {*|String} properties The specific property or map of properties to update. Any such existing
         *                              property will be overridden. If a map of properties, its json representation
         *                              will be merged into the existing properties.
         * @param {*}        value      The value to store if a single property is specified. The value will be
         *                              json formatted.
         *
         * @return {Boolean} True if the updates were synced with this storage, false otherwise. The in-memory storage
         *                   will still be updated even if the sync fails.
         */
        update(key, properties, value) {
            const currentProperties = this._getProperties(key);
            let newProperties = {};

            if (_.isObject(properties)) { // also true for null
                newProperties = properties || {};
            } else {
                newProperties[properties] = value;
            }

            this._attributes[key] = _.extend(currentProperties, newProperties);

            return this._sync(key);
        },

        /**
         * Returns the property or properties from the property sub-tree indexed by the specified key. <p>
         *
         * <tt>property</tt> can be a string for a single property and a single value will be returned, or it can
         * be an array of properties to be looked up, which will return an object with matching (key, value) pairs.
         *
         * If <tt>property</tt> is falsey, the entire sub-tree will be returned. The sub-tree is mutable, and changes
         * to it will be reflected in storage once <tt>update</tt>, <tt>unset</tt>, or <tt>remove</tt> is called. <p>
         *
         * All property values returned complies with a data type supported by json, e.g. string or number or array
         * of such.
         *
         * @param {String}          key           The key for the property sub-tree were to find the property or
         *                                        properties.
         * @param {*|String|Array} [property]     Property or array of properties we want to retrieve
         * @param {*}              [defaultValue] If <tt>property</tt> is specified as a string, the
         *                                        <tt>defaultValue</tt> will be returned if no such property exists.
         *
         * @return {*|String|*} The property, properties, or map of matching properties; or falsey if invalid
         *                      parameter types.
         */
        get(key, property, defaultValue) {
            const properties = this._getProperties(key);

            switch (true) {
                case arguments.length === 1:
                    return properties || {};

                case _.isString(property):
                    return properties.hasOwnProperty(property) ? properties[property] : defaultValue;

                case _.isArray(property):
                    return _.pick.apply(null, [properties].concat(property)) || {};

                default: // No log needed here.
                    break;
            }
        },

        /**
         * Un-sets (removes) a single property under the specified key. <p>
         *
         * If <tt>property</tt> is the last remaining property under <tt>key</tt>, the entire (empty) sub-tree
         * under <tt>key</tt> will be removed from storage altogether.
         *
         * @param {String} key      The key for the property sub-tree were <tt>property</tt> should be removed.
         * @param {String} property The property to remove.
         *
         * @return {Boolean} True if the updates were synced with this storage, false otherwise. The in-memory storage
         *                   will still be updated even if the sync fails.
         */
        unset(key, property) {
            const values = this._attributes[key];

            if (values && values.hasOwnProperty(property)) {
                delete values[property];
                // Other properties remain? If so, sync...
                for (property in values) {
                    if (values.hasOwnProperty(property)) {
                        return this._sync(key);
                    }
                }
                // No other properties remain, so we delete the key as to not leave traces behind client side...
                return this.remove(key);
            }

            // Return true since nothing to update, so we call it a success...
            return true;
        },

        /**
         * Removes the entire property sub-tree indexed by the supplied key, regardless of how many
         * properties are stored in it.
         *
         * @param {String} key The key for the property sub-tree to be removed altogether; cannot be falsey.
         *
         * @return {Boolean} True if the removals were synced with this storage, false otherwise. The in-memory storage
         *                   will still be updated even if the sync fails.
         */
        remove(key) {
            try {
                delete this._attributes[key];
                this._storage.removeItem(KEY_PREFIX + key);
                this._storage.removeItem(key); // TODO: legacy issue, remove eventually!

                return true;
            } catch (e) {
                return false;
            }
        }

    });

    return {

        from(store) {
            return new Storage(store);
        },

        /**
         * Local storage interface; never null.
         */
        Local: new Storage(Browser.getSafeStorage("localStorage")),

        /**
         * Session storage interface; never null.
         */
        Session: new Storage(Browser.getSafeStorage("sessionStorage")),

        /**
         * @type {String,String}
         * Keys indexing sub-trees of stored properties. <p>
         *
         * These are the keys supplied as the <tt>key</tt> argument to the <tt>get</tt>, <tt>update</tt>,
         * <tt>unset</tt>, and <tt>remove</tt> functions.
         */
        Keys: {
            COMMENTS:                      "comments",
            DASHBOARD:                     "dashboard",
            DASHBOARD_SORTING_PANEL:       "dashboardSortingPanel",
            EDITOR:                        "editor",
            EXPERIENCE_COLLECTION:         "experienceCollection",
            EXPERIENCE_OVERVIEW_VIEW:      "experienceOverviewView",
            IMAGE_COLLECTION:              "imageCollection",
            IMAGE_LIBRARY_SORTING_PANEL:   "imageLibrarySortingPanel",
            INTEGRATIONS:                  "integrations",
            LAST_USED_SOCIAL_PROPERTIES:   "lastUsedSocialProperties",
            LAST_USED_TEXT_PROPERTIES:     "lastUsedTextProperties",
            ALIGNED_TO_SCENE_CHECKBOX:     "alignedToSceneCheckbox",
            LAST_USED_BUTTON_PROPERTIES:   "lastUsedButtonProperties",
            LOGIN:                         "login",
            MASTER_LAYERS:                 "masterLayers",
            PROJECT_COLLECTION:            "projectCollection",
            PROJECT_DASHBOARD_EXPERIENCES: "projectDashboardExperiences",
            PUBLISH_VIEW:                  "publishView",
            RULERS_SHOWN:                  "rulersShown",
            SNAPPING_ENABLED:              "editorSnapping",
            TEMPLATE_SORTING_PANEL:        "templateSortingPanel",
            CREATOR_CONNECT:               "connect",
            ONLOAD_ACTION_ENABLED:         "onloadActionEnabled",
            ONLOAD_COMPONENT_INIT_COUNT:   "onloadComponentInitCount",
            BEHAVIOR_COMPONENT_INIT_COUNT: "behaviorComponentInitCount",
            CONTENT_CONTROL_BETA:          "contentControlBeta",
            NEW_DASHBOARD_EXP:             "newDashboardExperience"
        }
    };
});



// WEBPACK FOOTER //
// ./src/shared/services/Storage.js